import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Box, TextField, InputAdornment,
  Tabs, Tab, CircularProgress, Alert, Card, CardContent,
  Avatar, Chip, Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import { search as searchApi, SearchResult } from "../api/endpoints";
import type { User, Post, Company, Event } from "../types";
import { useLanguage } from "../context/LanguageContext";

type TabType = "all" | "users" | "posts" | "companies" | "events";
const TABS: TabType[] = ["all", "users", "posts", "companies", "events"];

export default function Search() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";

  const [query, setQuery] = useState(initialQ);
  const [tab, setTab] = useState<TabType>("all");
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = async (q: string, tabType: TabType) => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await searchApi.query(q, tabType === "all" ? undefined : tabType, 20);
      setResults(res ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.noResults"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (query) setSearchParams({ q: query });
      doSearch(query, tab);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, tab]);

  useEffect(() => {
    if (initialQ) doSearch(initialQ, "all");
  }, []);

  const totalCount =
    (results?.users?.length ?? 0) +
    (results?.posts?.length ?? 0) +
    (results?.companies?.length ?? 0) +
    (results?.events?.length ?? 0);

  const showUsers = tab === "all" || tab === "users";
  const showPosts = tab === "all" || tab === "posts";
  const showCompanies = tab === "all" || tab === "companies";
  const showEvents = tab === "all" || tab === "events";

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <TextField
        fullWidth
        placeholder={t("search.placeholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        autoFocus
        sx={{ mb: 2 }}
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {TABS.map((tabKey) => {
          const labelMap: Record<TabType, string> = {
            all: `${t("search.all")}${totalCount ? ` (${totalCount})` : ""}`,
            users: t("search.users"),
            posts: t("search.posts"),
            companies: t("search.companies"),
            events: t("search.events"),
          };
          return (
            <Tab
              key={tabKey}
              value={tabKey}
              label={labelMap[tabKey]}
              sx={{ minWidth: "auto", textTransform: "capitalize" }}
            />
          );
        })}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && results && (
        <Box>
          {/* Users */}
          {showUsers && results.users && results.users.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                {t("search.users")} ({results.users.length})
              </Typography>
              {results.users.map((u: User) => (
                <Card
                  key={u.id}
                  sx={{ mb: 1, cursor: "pointer" }}
                  onClick={() => navigate(`/app/profile/${u.username}`)}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 }, display: "flex", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                      {u.first_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {u.first_name} {u.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                      {u.bio && (
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {u.bio}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Posts */}
          {showPosts && results.posts && results.posts.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                {t("search.posts")} ({results.posts.length})
              </Typography>
              {results.posts.map((p: Post) => (
                <Card key={p.id} sx={{ mb: 1, cursor: "pointer" }} onClick={() => navigate(`/app/post/${p.id}`)}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Chip label={p.post_type.replace(/_/g, " ")} size="small" sx={{ mb: 0.5, fontSize: "0.65rem" }} />
                    <Typography variant="body2" sx={{
                      display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {p.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.creator ? `${p.creator.first_name} ${p.creator.last_name}` : t("common.anonymous")}
                      {" · "}{new Date(p.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Companies */}
          {showCompanies && results.companies && results.companies.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                {t("search.companies")} ({results.companies.length})
              </Typography>
              {results.companies.map((co: Company) => (
                <Card key={co.id} sx={{ mb: 1, cursor: "pointer" }} onClick={() => navigate(`/app/company/${co.id}`)}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 }, display: "flex", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{co.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{co.company_type}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {co.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Events */}
          {showEvents && results.events && results.events.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                {t("search.events")} ({results.events.length})
              </Typography>
              {results.events.map((ev: Event) => (
                <Card key={ev.id} sx={{ mb: 1, cursor: "pointer" }} onClick={() => navigate(`/app/events/${ev.id}`)}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 }, display: "flex", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                      <EventIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>{ev.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(ev.start_time).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {ev.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {totalCount === 0 && query && (
            <Typography color="text.secondary" textAlign="center" py={6}>
              {t("search.noResultsFor")} "{query}"
            </Typography>
          )}
        </Box>
      )}

      {!loading && !results && !query && (
        <Typography color="text.secondary" textAlign="center" py={6}>
          {t("search.startTyping")}
        </Typography>
      )}
    </Container>
  );
}
